import { ChevronLeft } from 'lucide-react'
import { Link } from 'react-router-dom'
import StatCard from './StatCard'
import './StatStrip.css'

/**
 * StatStrip — the one layout every page's KPI row uses.
 *
 * Ported from the rawam-ui reference panel. Each feature used to lay its own
 * `repeat(auto-fit, minmax(180px, 1fr))` grid, which is fine for four cards and
 * wrong for six: at 1440px the sixth dropped alone onto a second row and left a
 * hole beside it. `auto-fit` cannot know that six cards want one row of six, or
 * failing that two rows of three — it only knows a minimum width.
 *
 * So the COUNT leads: the strip declares how many cards it holds and the
 * stylesheet picks a column count that divides evenly at every width.
 *
 * The reference takes StatCards as children; this app's pages describe them as
 * data, so `items` is kept and mapped here. The rendered result is identical.
 *
 * @param {{id: string, label: string, value: string|number, hint?: string,
 *          icon?: import('react').ElementType, to?: string, selectable?: boolean,
 *          delta?: object, quiet?: boolean,
 *          tone?: 'neutral'|'accent'|'success'|'warning'|'danger'|'info'|'gold'
 *                 |'violet'|'rose'}[]} items  a tone is what the card MEANS; if
 *   two cards mean the same colour the strip moves the second one — see
 *   spreadTones() below.
 * @param {string} label  accessible name for the group
 * @param {string} [to]   destination for every card that does not name its own
 * @param {string} [activeId]  the card currently filtering the list below
 * @param {(id: string) => void} [onSelect]  makes the cards filter controls. A
 *   card opts out with `selectable: false`, which is how a strip mixes a filter
 *   ("suspended") with a plain total that has nothing to narrow to.
 * @param {boolean} [loading]
 * @param {'cards'|'band'} [variant]  `band` is one surface holding the figures
 *   inline, for a SECONDARY group standing next to a primary strip. Two card
 *   grids of different counts stacked on each other never line up — five tiles
 *   over four is visibly ragged no matter how evenly each row divides — and a
 *   group of four figures that are all empty has no business claiming four
 *   cards' worth of the page either.
 */
/**
 * Which hue a tone actually IS, so two tones that read the same at chip size
 * count as one. `accent` and `success` are both green; `warning` and `gold` are
 * both amber. A strip that asked for accent + success used to put two green
 * chips side by side and lose the distinction it was drawing.
 */
const FAMILY = {
  accent: 'green',
  success: 'green',
  warning: 'amber',
  gold: 'amber',
  danger: 'red',
  info: 'blue',
  violet: 'violet',
  rose: 'rose',
  neutral: 'grey',
}

/**
 * What a card falls back to when the hue it asked for is already spoken for,
 * in the order it is reached for. Green leads because it is the identity, then
 * the two hues that carry no claim, then amber, and grey only once a strip has
 * run out of colours — which at six cards it never does.
 */
const SPARE = ['accent', 'info', 'violet', 'gold', 'rose', 'danger', 'neutral']

/**
 * The two tones that are not a claim about the figure. `accent` is what a card
 * gets when nobody chose — the strip's "total" wears it on every page — and
 * `neutral` is what a page reaches for when a figure has nothing to say about
 * itself. Neither is a meaning worth defending, so both are made to give way.
 */
const UNCLAIMED = new Set(['accent', 'neutral'])

/**
 * Give every card in a strip a hue no other card is wearing.
 *
 * Meaning is settled first, and it is never moved: a strip that says "passed"
 * in green and "failed" in red keeps both, whatever else it holds. Only then
 * are the cards that never meant a colour dealt the families nobody claimed.
 *
 * That order is the whole point. Assigning left-to-right instead let the total
 * — accent, green, on every page — take green before "passed" could ask for it,
 * and passed came out blue. The card that meant nothing won, and the one that
 * meant something paid for it.
 *
 * A page wanting one deliberately grey card asks for `quiet`, which greys it
 * outright and is what a zero already does on its own.
 */
function spreadTones(items) {
  const taken = new Set()
  const tones = new Array(items.length)

  items.forEach((item, index) => {
    const asked = item.tone
    if (!asked || UNCLAIMED.has(asked)) return
    const family = FAMILY[asked] ?? 'green'
    if (taken.has(family)) return
    taken.add(family)
    tones[index] = asked
  })

  return items.map((item, index) => {
    if (tones[index]) return item
    const free = SPARE.find((tone) => !taken.has(FAMILY[tone]))
    if (!free) return item
    taken.add(FAMILY[free])
    return { ...item, tone: free }
  })
}

export default function StatStrip({
  items,
  label,
  to,
  activeId,
  onSelect,
  loading = false,
  variant = 'cards',
  className = '',
}) {
  if (variant === 'band') {
    const Tag = to ? Link : 'div'
    /* THE SHARE, computed here rather than asked for.
       A band is always a distribution — a population split across risk levels,
       or a run of notifications split across states — and a distribution that
       prints only its counts makes the reader do the division. It is only
       meaningful when every figure is a countable part of one whole, so a band
       carrying a percentage or a dash simply does not draw one. */
    const countable =
      items.length > 1 && items.every((item) => typeof item.value === 'number' && item.value >= 0)
    const total = countable ? items.reduce((sum, item) => sum + item.value, 0) : 0

    return (
      <Tag
        className={`stat-band ${className}`.trim()}
        to={to || undefined}
        role={to ? undefined : 'group'}
        aria-label={label}
      >
        <span className="stat-band__items">
          {items.map(({ id, icon: Icon, label: itemLabel, value, tone }) => (
            <span key={id} className={`stat-band__item${tone ? ` stat-band__item--${tone}` : ''}`}>
              {/* ONE object carries all three things this cell has to say: the
                  category's colour, its glyph, and its share of the whole. The
                  share used to be a bar under the figure, which meant a disc AND
                  a bar AND a number — three marks for one fact. A ring drawn
                  around the glyph is the same reading in one place. */}
              <span
                className="stat-band__mark"
                style={
                  countable && total > 0
                    ? { '--share': Math.round((value / total) * 100) }
                    : undefined
                }
                aria-hidden="true"
              >
                <span className="stat-band__mark-face">
                  {Icon ? <Icon size={18} strokeWidth={1.9} /> : null}
                </span>
              </span>

              <span className="stat-band__text">
                <bdi className="stat-band__label">{itemLabel}</bdi>
                <bdi className="stat-band__value">
                  {typeof value === 'number' ? value.toLocaleString('en-US') : value}
                </bdi>
              </span>
            </span>
          ))}
        </span>
        {/* Points left, which is FORWARD in RTL — so it is not rtl-flipped. */}
        {to ? (
          <ChevronLeft className="stat-band__go" size={16} strokeWidth={2} aria-hidden="true" />
        ) : null}
      </Tag>
    )
  }

  const canSelect = (item) => onSelect && item.selectable !== false && !item.to && !to
  /*
   * A MIXED STRIP is the only one that has to tell its cards apart.
   *
   * `Total users` on the groups page counts USERS while the cards beside it filter GROUPS,
   * so it opts out of selection — and looked exactly like the three that do not. Where a
   * strip mixes the two, the readouts drop their tile and their tone (see
   * `stat-card--readout`); where NOTHING is pressable — the dashboard KPIs, the phishing
   * funnel, a programme's report — there is nothing to contrast against and the cards stay
   * cards.
   */
  const mixed = items.some(canSelect) && items.some((item) => !canSelect(item))

  return (
    <div
      className={`stat-strip ${className}`.trim()}
      data-count={Math.min(Math.max(items.length, 1), 6)}
      role="group"
      aria-label={label}
    >
      {spreadTones(items).map((item) => {
        const selectable = canSelect(item)
        return (
          <StatCard
            key={item.id}
            label={item.label}
            value={typeof item.value === 'number' ? item.value.toLocaleString('en-US') : item.value}
            hint={item.hint}
            icon={item.icon}
            tone={item.tone ?? 'accent'}
            delta={item.delta}
            /* A card whose figure is zero or absent steps back on its own, so no
               page has to remember to ask for it. */
            quiet={item.quiet ?? isEmpty(item.value)}
            loading={loading}
            to={item.to ?? to}
            onClick={selectable ? () => onSelect(item.id) : undefined}
            readout={mixed && !selectable}
            active={activeId === item.id}
            data-stat-key={item.id}
          >
            {/* StatCard's footer slot — the dashboard KPI shares put their meter
                here, under the figure it describes. */}
            {item.children}
          </StatCard>
        )
      })}
    </div>
  )
}

/** A metric with nothing behind it yet. */
const isEmpty = (value) => value === 0 || value == null || value === '—' || value === ''
